import { useState, useRef, useEffect, useCallback } from 'react'
import { AgentsMdModal } from './AgentsMdModal'
import { ChevronDown, Share2, MoreHorizontal, Bell, Users, ChevronLeft, Zap, Globe, Sparkles, Lock, Check, X } from 'lucide-react'
import { getSupabase } from '@/hooks/useAuth'
import { useApp } from '@/store/AppContext'
import { getTranslations } from '@/i18n'
import { UserAvatarDropdown } from './UserAvatarDropdown'
import { CollaboratePanel } from './CollaboratePanel'
import { HeaderContextMenu } from './HeaderContextMenu'
import { TaskDetailsModal } from './TaskDetailsModal'
import { buildShareLink, markConversationPublic } from '@/services/conversationService'
import type { SineModel } from '@/types/index'
import { useAuth } from '@/hooks/useAuth'
import { useCredits } from '@/hooks/useCredits'
import { useNav } from '@/App'
import type { WorkspaceSurface } from '@/types'

interface HeaderProps {
  onMobileBack?: () => void
}

// ── Model selector popup (Manus-style) ────────────────────────
function HeaderModelSelector() {
  const { settings, updateSettings } = useApp()
  const t = getTranslations(settings.language)
  const { user } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const isPro = profile?.plan === 'pro'
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const model = settings.model
  const normalizedModel: SineModel = model === 'sine-1' ? 'sine-lite' : model

  const tiers: { id: SineModel; name: string; desc: string; model: string; icon: React.ReactNode; proOnly: boolean }[] = [
    { id: 'sine-lite', name: 'Sine Lite',  desc: 'Rask og effektiv',               model: 'Qwen 3.5 Flash',    icon: <Zap size={13} />,      proOnly: false },
    { id: 'sine-pro',  name: 'Sine Pro',   desc: t.layout.powerfulForComplex, model: 'Claude Sonnet 4.6', icon: <Globe size={13} />,    proOnly: !isPro  },
    { id: 'sine-max',  name: 'Sine Max',   desc: 'Maks ytelse',                    model: 'Claude Opus 4.7',   icon: <Sparkles size={13} />, proOnly: !isPro  },
  ]

  const currentTier = tiers.find(t => t.id === normalizedModel) ?? tiers[0]

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={`header-model-btn${open ? ' is-open' : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <span>{currentTier.name}</span>
        <ChevronDown
          className="header-model-chevron"
          size={13}
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open && (
        <div className="header-model-popup">
          {tiers.map(({ id, name, desc, model, icon, proOnly }) => {
            const isActive = normalizedModel === id
            return (
              <button
                key={id}
                className={`header-model-option${isActive ? ' is-active' : ''}${proOnly ? ' is-locked' : ''}`}
                onClick={() => {
                  if (!proOnly) { updateSettings({ model: id }); setOpen(false) }
                }}
                title={proOnly ? 'Krever Sine Pro-abonnement' : undefined}
              >
                <span className="header-model-option-icon">{icon}</span>
                <div className="header-model-option-body">
                  <div className="header-model-option-name">
                    {name}
                    {proOnly && <Lock className="header-model-lock" size={10} />}
                  </div>
                  <div className="header-model-option-desc">{desc}</div>
                  <div className="header-model-option-model">{model}</div>
                </div>
                {isActive && !proOnly && <Check className="header-model-check" size={13} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
// ── 3-way mode toggle: Chat / Agent / Studio ────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ModeToggle() {
  const { surface, setSurface } = useNav()
  const { activeConversation, settings } = useApp()
  const t = getTranslations(settings.language)
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })
  // Lock mode once conversation has messages (prevent accidental mode switching)
  const isLocked = Boolean(activeConversation && activeConversation.messages.length > 0)
  // Unified: keep the technical surfaces, but present them as one Sine experience.
  const modes: { id: WorkspaceSurface; label: string; title: string }[] = [
    { id: 'chat',   label: settings.language === 'en' ? 'Talk' : 'Samtale',   title: t.layout.chatMode },
    { id: 'agent',  label: settings.language === 'en' ? 'Work' : 'Arbeid',  title: t.layout.agentMode },
  ]
  const activeIndex = modes.findIndex(m => m.id === surface)
  useEffect(() => {
    const btn = btnRefs.current[activeIndex]
    if (btn) {
      setIndicatorStyle({ left: btn.offsetLeft, width: btn.offsetWidth })
    }
  }, [activeIndex, isLocked])
  return (
    <div className="header-mode-toggle-wrap">
      <div
        className={`header-mode-toggle-bg${isLocked ? ' locked' : ''}`}
        style={{ position: 'relative' }}
      >
        {/* Sliding indicator */}
        <div
          className="header-mode-toggle-indicator"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: indicatorStyle.left,
            width: indicatorStyle.width,
            borderRadius: 10,
            background: '#2A2A2A',
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)',
            pointerEvents: 'none',
          }}
        />
        {modes.map(({ id, label, title }, i) => {
          const isActive = surface === id
          const isHidden = isLocked && !isActive
          return (
            <button
              key={id}
              ref={el => { btnRefs.current[i] = el }}
              className={`header-mode-toggle-btn${isActive ? ' active' : ''}${isHidden ? ' hidden-mode' : ''}`}
              onClick={() => !isLocked && setSurface(id)}
              title={isLocked ? `${title} (låst — start ny samtale for å bytte)` : title}
              style={{
                position: 'relative',
                zIndex: 1,
                pointerEvents: isLocked ? 'none' : 'auto',
              }}
            >
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
// ── Inline rename modal ──────────────────────────────────────
function RenameModal({
  initialTitle, language, onConfirm, onCancel,
}: { initialTitle: string; language: 'no' | 'en'; onConfirm: (t: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initialTitle)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { inputRef.current?.select() }, [])
  const submit = () => { if (value.trim()) onConfirm(value.trim()) }
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onCancel}>
      <div style={{
        background: 'var(--ctx-bg, #1E1E1E)',
        border: '1px solid var(--ctx-border, #2E2E2E)',
        borderRadius: 14, padding: '24px', width: 380, maxWidth: '92vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        position: 'relative',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onCancel} style={{
          position: 'absolute', top: 12, right: 12,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--ctx-text, #9A9A9A)', padding: 4, borderRadius: 6,
          display: 'flex', alignItems: 'center',
        }}><X size={15} /></button>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: 'var(--ctx-text, #E5E5E5)' }}>
          {t.layout.rename}
        </h3>
        <input
          ref={inputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel() }}
          style={{
            width: '100%', padding: '9px 12px',
            background: 'var(--ctx-hover, #2A2A2A)',
            border: '1px solid var(--ctx-border, #3A3A3A)',
            borderRadius: 8, color: 'var(--ctx-text, #E5E5E5)',
            fontSize: 14, fontFamily: 'inherit', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onCancel} style={{
            padding: '7px 16px', borderRadius: 8,
            border: '1px solid var(--ctx-border, #3A3A3A)',
            background: 'transparent', color: 'var(--ctx-text, #C0C0C0)',
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}>{language === 'no' ? 'Avbryt' : 'Cancel'}</button>
          <button onClick={submit} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none',
            background: '#1A93FE', color: '#fff',
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
          }}>{t.settings.save}</button>
        </div>
      </div>
    </div>
  )
}

export function Header({ onMobileBack }: HeaderProps) {
  const {
    activeConversation, settings, openSettingsTab,
    toggleFavorite, renameConversation, deleteConversation,
    setActiveConversationId,
  } = useApp()
  const { user } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const t = getTranslations(settings.language)

  const formatCredits = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return n.toString()
  }
  const [collaborateOpen, setCollaborateOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [agentsMdOpen, setAgentsMdOpen] = useState(false)
  const [bellOpen, setBellOpen] = useState(false)
  const [notifications, setNotifications] = useState<{id:string;title:string;message:string;image_url?:string;read:boolean;created_at:string}[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const bellRef = useRef<HTMLButtonElement>(null)
  const bellPopupRef = useRef<HTMLDivElement>(null)
  const collaborateRef = useRef<HTMLButtonElement>(null)
  const moreRef = useRef<HTMLButtonElement>(null)

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return
    const sb = getSupabase()
    const { data } = await sb.from('notifications').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(20)
    if (data) {
      setNotifications(data)
      setUnreadCount(data.filter((n: {read: boolean}) => !n.read).length)
    }
  }, [user?.id])

  const markAllRead = async () => {
    if (!user?.id) return
    const sb = getSupabase()
    await sb.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }

  useEffect(() => { loadNotifications() }, [loadNotifications])

  useEffect(() => {
    if (!bellOpen) return
    const handler = (e: MouseEvent) => {
      if (bellRef.current?.contains(e.target as Node)) return
      if (bellPopupRef.current?.contains(e.target as Node)) return
      setBellOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [bellOpen])

  const handleShare = async () => {
    if (!activeConversation) return
    await markConversationPublic(activeConversation.id)
    const link = buildShareLink(activeConversation.id)
    navigator.clipboard.writeText(link).catch(() => {})
  }

  // Copy authenticated deep-link (/c/{id}) for sharing with other logged-in users
  const handleCopyDeepLink = () => {
    if (!activeConversation) return
    const link = `${window.location.origin}/c/${activeConversation.id}`
    navigator.clipboard.writeText(link).catch(() => {})
  }

  const handleDelete = () => {
    if (!activeConversation) return
    setActiveConversationId(null)
    deleteConversation(activeConversation.id)
  }

  return (
    <>
      <header className="header">
        {/* Left: back button (mobile) + model selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            className="mobile-back-btn"
            onClick={onMobileBack}
            aria-label={t.layout.back}
          >
            <ChevronLeft size={20} />
            <span style={{ fontSize: 16 }}>{t.layout.back}</span>
          </button>
          <HeaderModelSelector />
        </div>

        {/* Right: actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Mobile credits pill */}
          {profile && (
            <button className="header-credits-btn mobile-credits-pill" title="Kreditter">
              <Sparkles size={12} style={{ color: '#FFD60A' }} />
              <span>{formatCredits(profile.credits)}</span>
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button
              ref={bellRef}
              className="header-icon-btn header-bell-btn"
              title={t.app.notifications}
              onClick={() => { setBellOpen(v => !v); if (!bellOpen) loadNotifications() }}
              style={bellOpen ? { background: '#1E1E1E', color: '#E5E5E5' } : {}}
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#FF3B30', border: '1.5px solid var(--header-bg, #141414)'
                }} />
              )}
            </button>
            {bellOpen && (
              <div ref={bellPopupRef} className="bell-popup">
                <div className="bell-popup-header">
                  <span>{t.app.notifications}</span>
                  {unreadCount > 0 && (
                    <button className="bell-mark-read" onClick={markAllRead}>Merk alle som lest</button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="bell-empty"><Bell size={22} /><p>Ingen varsler</p></div>
                ) : (
                  <div className="bell-list">
                    {notifications.map(n => (
                      <div key={n.id} className={`bell-item${n.read ? ' read' : ''}`}>
                        {n.image_url && <img src={n.image_url} alt="" className="bell-item-img" />}
                        <div className="bell-item-body">
                          <div className="bell-item-title">{n.title}</div>
                          <div className="bell-item-msg">{n.message}</div>
                          <div className="bell-item-time">{new Date(n.created_at).toLocaleString('nb-NO', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}</div>
                        </div>
                        {!n.read && <div className="bell-item-dot" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {activeConversation && (
            <>
              <button
                ref={collaborateRef}
                className="header-action-btn"
                onClick={() => setCollaborateOpen(v => !v)}
                style={collaborateOpen ? { background: '#1E1E1E', color: '#E5E5E5' } : {}}
              >
                <Users size={16} />
                <span>{t.app.collaborate}</span>
              </button>
              <button className="header-action-btn" onClick={handleShare}>
                <Share2 size={16} />
                <span>{t.app.share}</span>
              </button>
              <button
                ref={moreRef}
                className="header-icon-btn"
                title="Mer"
                onClick={() => setMenuOpen(v => !v)}
                style={menuOpen ? { background: '#1E1E1E', color: '#E5E5E5' } : {}}
              >
                <MoreHorizontal size={18} />
              </button>
            </>
          )}

          {/* Bruker-avatar med dropdown */}
          <div style={{ marginLeft: 4 }}>
            <UserAvatarDropdown onOpenSettings={(tab) => openSettingsTab(tab)} />
          </div>
        </div>

        {/* Collaborate panel */}
        <CollaboratePanel
          open={collaborateOpen}
          onClose={() => setCollaborateOpen(false)}
          anchorRef={collaborateRef as React.RefObject<HTMLElement>}
        />
      </header>

      {/* Header context menu */}
      {menuOpen && activeConversation && (
        <HeaderContextMenu
          isFavorite={activeConversation.isFavorite ?? false}
          anchorRef={moreRef as React.RefObject<HTMLElement>}
          language={settings.language}
          onClose={() => setMenuOpen(false)}
          onRename={() => { setMenuOpen(false); setRenameOpen(true) }}
          onToggleFavorite={() => toggleFavorite(activeConversation.id)}
          onTaskDetails={() => setTaskDetailsOpen(true)}
          onDelete={handleDelete}
          onCopyLink={handleCopyDeepLink}
          onAgentsMd={() => setAgentsMdOpen(true)}
        />
      )}

      {/* Task details modal */}
      {taskDetailsOpen && activeConversation && (
        <TaskDetailsModal
          conversation={activeConversation}
          language={settings.language}
          onClose={() => setTaskDetailsOpen(false)}
          onRename={(title) => renameConversation(activeConversation.id, title)}
        />
      )}

      {/* AGENTS.md editor modal */}
      {agentsMdOpen && activeConversation && (
        <AgentsMdModal
          conversationId={activeConversation.id}
          language={settings.language}
          onClose={() => setAgentsMdOpen(false)}
        />
      )}

      {/* Rename modal */}
      {renameOpen && activeConversation && (
        <RenameModal
          initialTitle={activeConversation.title}
          language={settings.language}
          onConfirm={(title) => { renameConversation(activeConversation.id, title); setRenameOpen(false) }}
          onCancel={() => setRenameOpen(false)}
        />
      )}
    </>
  )
}
